import { useState, useEffect } from 'react';
import type {
  RegulatoryAlert, RegulatoryPolicy, NotificationCampaign,
  AlertStatus, ActionType, ActionSchedule, NotifyTier, NotifyMethod,
  CampaignStatus, RegulatoryAction,
} from '../../../types/regulatory';
import {
  getAllAlerts, saveAlert,
  getAllPolicies,
  getAllCampaigns, saveCampaign, deleteCampaign,
} from '../../../services/firestore/regulatoryService';

// ── BRAND PALETTE (light theme — matches landing page) ──
const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  accent: '#E11D48', accentSoft: '#FFF1F2', accentLight: '#FB7185',
  bg: '#FFFFFF', bgSoft: '#F8FAFC', bgDark: '#F4F4F5',
  border: '#E4E4E7', borderLight: '#F4F4F5',
  btn: '#18181B', btnHover: '#27272A',
  success: '#059669', amber: '#D97706',
};

// ── NOTIFICATION TIERS ──
const NOTIFY_TIERS: Record<NotifyTier, { label: string; description: string; methods: NotifyMethod[]; noticeDays: number; color: string; bg: string }> = {
  minor: {
    label: 'Minor',
    description: 'Typo fixes, clarifications, formatting',
    methods: ['email'],
    noticeDays: 0,
    color: '#2563EB',
    bg: '#DBEAFE',
  },
  moderate: {
    label: 'Moderate',
    description: 'New sections, expanded data usage, partner additions',
    methods: ['email', 'banner'],
    noticeDays: 14,
    color: '#D97706',
    bg: '#FEF3C7',
  },
  material: {
    label: 'Material',
    description: 'Health data handling, consent model, new data sharing, rights changes',
    methods: ['email', 'banner', 'reconsent'],
    noticeDays: 30,
    color: '#DC2626',
    bg: '#FEF2F2',
  },
};

const METHOD_LABELS: Record<NotifyMethod, { icon: string; label: string; desc: string }> = {
  email: { icon: '\u2709', label: 'Email Blast', desc: 'Notify all active users via email' },
  banner: { icon: '\uD83D\uDD14', label: 'In-App Banner', desc: 'Persistent banner on next login until dismissed' },
  reconsent: { icon: '\uD83D\uDD12', label: 'Re-Consent Gate', desc: 'Must accept updated terms to continue using app' },
};

const CATEGORIES: Record<string, { label: string; color: string }> = {
  mhmda: { label: 'MHMDA', color: '#A78BFA' },
  hipaa: { label: 'HIPAA', color: '#22D3EE' },
  fda_ai: { label: 'FDA / AI', color: '#2DD4BF' },
  ftc_enforcement: { label: 'FTC', color: '#FBBF24' },
  state_privacy: { label: 'State Privacy', color: '#60A5FA' },
  telehealth_rx: { label: 'Telehealth/Rx', color: '#F472B6' },
};

const URGENCY: Record<string, { label: string; color: string; bg: string; border: string }> = {
  critical: { label: 'CRITICAL', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  high: { label: 'HIGH', color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
  medium: { label: 'MEDIUM', color: '#D97706', bg: '#FEF3C7', border: '#FDE68A' },
  low: { label: 'LOW', color: '#2563EB', bg: '#DBEAFE', border: '#BFDBFE' },
  informational: { label: 'INFO', color: C.textMuted, bg: C.borderLight, border: C.border },
};

const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: '#52525B', bg: '#F4F4F5' },
  reviewed: { label: 'Reviewed', color: '#2563EB', bg: '#DBEAFE' },
  in_pipeline: { label: 'In Pipeline', color: '#D97706', bg: '#FEF3C7' },
  resolved: { label: 'Resolved', color: '#059669', bg: '#D1FAE5' },
  dismissed: { label: 'Dismissed', color: '#A1A1AA', bg: '#F4F4F5' },
};

const ACTION_TYPES: Record<string, { label: string; icon: string; color: string }> = {
  policy_update: { label: 'Policy Update', icon: '\uD83D\uDCCB', color: '#A78BFA' },
  code_update: { label: 'Code Update', icon: '\u2699\uFE0F', color: '#22D3EE' },
  ad_marketing: { label: 'Ad / Marketing', icon: '\uD83D\uDCE2', color: '#FBBF24' },
  legal_review: { label: 'Legal Review', icon: '\u2696\uFE0F', color: '#F87171' },
  consent_flow: { label: 'Consent Flow', icon: '\uD83D\uDD10', color: '#4ADE80' },
  vendor_baa: { label: 'Vendor / BAA', icon: '\uD83E\uDD1D', color: '#60A5FA' },
};

const SYSTEM_LABELS: Record<string, string> = {
  consent_flows: 'Consent Flows', data_storage: 'Data Storage', data_deletion: 'Data Deletion',
  ai_pipeline: 'AI Pipeline', rx_handoff: 'Rx Handoff', supplement_sales: 'Supplement Sales',
  analytics_tracking: 'Analytics/Tracking', privacy_policy: 'Privacy Policy', chd_policy: 'CHD Policy',
  hipaa_npp: 'HIPAA NPP', baa: 'BAA', breach_notification: 'Breach Notification',
  user_rights: 'User Rights', admin_reporting: 'Admin Reporting', marketing: 'Marketing',
};

// ── SOURCES DATA (static) ──
const SOURCES = [
  { name: 'Federal Register \u2014 HHS', status: 'ok', last: '2026-02-09 23:04', items: 3, error: '' },
  { name: 'Federal Register \u2014 FTC', status: 'ok', last: '2026-02-09 23:05', items: 1, error: '' },
  { name: 'FDA Digital Health Center', status: 'ok', last: '2026-02-09 23:06', items: 2, error: '' },
  { name: 'HHS HIPAA Newsroom', status: 'ok', last: '2026-02-09 23:07', items: 0, error: '' },
  { name: 'FTC Health Privacy', status: 'ok', last: '2026-02-09 23:08', items: 1, error: '' },
  { name: 'Congress.gov Health Data Bills', status: 'ok', last: '2026-02-09 23:09', items: 1, error: '' },
  { name: 'IAPP Health Privacy News', status: 'ok', last: '2026-02-09 23:10', items: 4, error: '' },
  { name: 'Washington AG \u2014 MHMDA', status: 'ok', last: '2026-02-09 23:11', items: 0, error: '' },
  { name: 'California AG \u2014 CCPA', status: 'ok', last: '2026-02-09 23:12', items: 0, error: '' },
  { name: 'Florida Legislature', status: 'degraded', last: '2026-02-02 23:04', items: 0, error: '403 Forbidden \u2014 page structure changed, selector needs update' },
  { name: 'NY Legislature', status: 'ok', last: '2026-02-09 23:14', items: 1, error: '' },
  { name: 'NCSL Privacy Tracker', status: 'ok', last: '2026-02-09 23:15', items: 2, error: '' },
];

const URGENCY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 };

// ── STYLES ──
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap');
  .rd * { box-sizing: border-box; }
  .rd { font-family: 'DM Sans', sans-serif; background: #0D0608; color: ${C.text}; min-height: 100vh; font-size: 14px; line-height: 1.5; padding: 20px; border-radius: 12px; }
  .rd .header { background: ${C.bg}; border-bottom: 1px solid ${C.border}; padding: 20px 32px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 50; }
  .rd .logo { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 500; letter-spacing: 2px; }
  .rd .logo span { background: linear-gradient(135deg, ${C.accent}, ${C.accentLight}); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .rd .header-badge { font-size: 11px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: ${C.accent}; background: ${C.accentSoft}; padding: 4px 12px; border-radius: 100px; }
  .rd .tabs { display: flex; gap: 0; background: ${C.bg}; border-bottom: 1px solid ${C.border}; padding: 0 32px; }
  .rd .tab { padding: 14px 20px; font-size: 13px; font-weight: 500; color: ${C.textMuted}; cursor: pointer; border: none; background: none; border-bottom: 2px solid transparent; transition: all 0.2s; }
  .rd .tab:hover { color: ${C.textSec}; }
  .rd .tab.active { color: ${C.accent}; border-bottom-color: ${C.accent}; }
  .rd .tab .badge { font-size: 10px; font-weight: 700; background: ${C.accent}; color: #F5EDE8; padding: 1px 6px; border-radius: 10px; margin-left: 6px; }
  .rd .content { padding: 24px 32px; max-width: 1400px; margin: 0 auto; }
  .rd .card { background: ${C.bg}; border: 1px solid ${C.border}; border-radius: 12px; overflow: hidden; }
  .rd .card-header { padding: 16px 20px; border-bottom: 1px solid ${C.borderLight}; display: flex; align-items: center; justify-content: space-between; }
  .rd .card-header h3 { font-size: 14px; font-weight: 600; color: #C9A96E; margin: 0; font-family: 'Playfair Display', serif; }
  .rd .alert-row { display: flex; align-items: center; gap: 16px; padding: 14px 20px; border-bottom: 1px solid ${C.borderLight}; cursor: pointer; transition: background 0.15s; }
  .rd .alert-row:hover { background: ${C.bgSoft}; }
  .rd .alert-row:last-child { border-bottom: none; }
  .rd .pill { display: inline-flex; align-items: center; font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; padding: 3px 10px; border-radius: 100px; white-space: nowrap; }
  .rd .status-pill { font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; padding: 3px 10px; border-radius: 100px; }
  .rd .detail-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(0,0,0,0.6); z-index: 100; display: flex; justify-content: flex-end; }
  .rd .detail-panel { width: 640px; max-width: 90vw; background: ${C.bg}; height: 100%; overflow-y: auto; border-left: 1px solid ${C.border}; animation: rdSlideIn 0.2s ease; }
  @keyframes rdSlideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  .rd .detail-header { padding: 24px; border-bottom: 1px solid ${C.border}; position: sticky; top: 0; background: ${C.bg}; z-index: 10; }
  .rd .detail-body { padding: 24px; }
  .rd .detail-section { margin-bottom: 28px; }
  .rd .detail-section-title { font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: ${C.textMuted}; margin-bottom: 10px; }
  .rd .detail-text { font-size: 13px; color: ${C.textSec}; line-height: 1.7; }
  .rd .action-item { display: flex; align-items: flex-start; gap: 12px; padding: 12px 14px; background: ${C.bgSoft}; border: 1px solid ${C.borderLight}; border-radius: 8px; margin-bottom: 8px; }
  .rd .action-tag { font-size: 10px; font-weight: 500; padding: 2px 8px; border-radius: 4px; background: ${C.borderLight}; color: ${C.textSec}; }
  .rd .notify-card { border: 1px solid ${C.border}; overflow: hidden; margin-bottom: 0; border-radius: 0; border-left: none; border-right: none; border-top: none; }
  .rd .notify-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .rd .notify-pipeline { display: flex; align-items: center; gap: 0; margin: 16px 0; position: relative; }
  .rd .pipeline-step { flex: 1; text-align: center; position: relative; z-index: 2; }
  .rd .pipeline-dot { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 6px; font-size: 13px; font-weight: 700; border: 2px solid ${C.border}; background: ${C.bg}; color: ${C.textMuted}; transition: all 0.2s; }
  .rd .pipeline-dot.active { border-color: ${C.accent}; background: ${C.accentSoft}; color: ${C.accent}; }
  .rd .pipeline-dot.complete { border-color: ${C.success}; background: rgba(74,222,128,0.12); color: ${C.success}; }
  .rd .pipeline-label { font-size: 10px; font-weight: 500; color: ${C.textMuted}; letter-spacing: 0.3px; }
  .rd .pipeline-label.active { color: ${C.accent}; font-weight: 600; }
  .rd .pipeline-label.complete { color: ${C.success}; }
  .rd .pipeline-line { position: absolute; top: 16px; left: 10%; right: 10%; height: 2px; background: ${C.border}; z-index: 1; }
  .rd .pipeline-line-fill { height: 100%; background: ${C.success}; transition: width 0.3s ease; }
  .rd .tier-option { padding: 14px 16px; border: 2px solid ${C.border}; border-radius: 10px; cursor: pointer; transition: all 0.15s; margin-bottom: 8px; }
  .rd .tier-option:hover { border-color: ${C.textMuted}; }
  .rd .tier-option.selected { border-color: ${C.accent}; background: ${C.accentSoft}; }
  .rd .method-chip { font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 4px; display: inline-flex; align-items: center; gap: 4px; }
  .rd .method-chip.active { background: rgba(74,222,128,0.12); color: #4ADE80; }
  .rd .method-chip.inactive { background: ${C.borderLight}; color: ${C.textMuted}; }
  .rd .progress-bar { height: 8px; background: ${C.borderLight}; border-radius: 4px; overflow: hidden; }
  .rd .progress-fill { height: 100%; border-radius: 4px; transition: width 0.3s ease; }
  .rd .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: center; justify-content: center; }
  .rd .modal { background: ${C.bg}; border-radius: 16px; padding: 28px; width: 520px; max-width: 90vw; max-height: 85vh; overflow-y: auto; animation: rdFadeUp 0.2s ease; }
  @keyframes rdFadeUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  .rd .modal h3 { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 400; margin: 0 0 20px; color: #C9A96E; }
  .rd .form-group { margin-bottom: 16px; }
  .rd .form-label { font-size: 11px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: ${C.textMuted}; margin-bottom: 6px; display: block; }
  .rd .form-input { width: 100%; padding: 10px 14px; font-size: 13px; font-family: 'DM Sans', sans-serif; border: 1px solid ${C.border}; border-radius: 8px; background: #1A0A0F; color: ${C.text}; outline: none; }
  .rd .form-input:focus { border-color: ${C.accent}; }
  .rd textarea.form-input { min-height: 80px; resize: vertical; }
  .rd .form-select { width: 100%; padding: 10px 14px; font-size: 13px; font-family: 'DM Sans', sans-serif; border: 1px solid ${C.border}; border-radius: 8px; background: #1A0A0F; color: ${C.text}; outline: none; cursor: pointer; }
  .rd .btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; font-size: 12px; font-weight: 600; font-family: 'DM Sans', sans-serif; border: none; border-radius: 8px; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
  .rd .btn-primary { background: ${C.btn}; color: #0D0608; }
  .rd .btn-primary:hover { background: ${C.btnHover}; }
  .rd .btn-accent { background: ${C.accent}; color: #F5EDE8; }
  .rd .btn-accent:hover { background: #A02E4E; }
  .rd .btn-ghost { background: transparent; color: ${C.textSec}; border: 1px solid ${C.border}; }
  .rd .btn-ghost:hover { background: #2A1118; }
  .rd .btn-sm { padding: 5px 10px; font-size: 11px; }
  .rd .toast { position: fixed; bottom: 24px; right: 24px; background: ${C.btn}; color: #0D0608; padding: 12px 20px; border-radius: 10px; font-size: 13px; font-weight: 500; z-index: 300; animation: rdFadeUp 0.2s ease; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
  .rd .source-row { display: flex; align-items: center; gap: 16px; padding: 12px 16px; background: ${C.bg}; border-radius: 6px; border: 1px solid ${C.borderLight}; transition: all .15s; margin-bottom: 6px; }
  .rd .source-row:hover { border-color: ${C.border}; }
  .rd .calendar-item { display: flex; gap: 16px; padding: 14px 18px; border-left: 3px solid; margin-bottom: 2px; background: ${C.bg}; border-radius: 0 6px 6px 0; cursor: pointer; transition: all .15s; }
  .rd .calendar-item:hover { box-shadow: 0 2px 8px rgba(0,0,0,.05); transform: translateX(2px); }
  @media (max-width: 768px) {
    .rd .content { padding: 16px; }
    .rd .header { padding: 16px; }
    .rd .tabs { padding: 0 16px; overflow-x: auto; }
    .rd .detail-panel { width: 100%; max-width: 100%; }
  }
`;

// ── HELPER COMPONENTS ──
function Pill({ children, color, bg }: { children: React.ReactNode; color: string; bg: string }) {
  return <span className="pill" style={{ color, background: bg }}>{children}</span>;
}

function StatusPill({ status }: { status: string }) {
  const s = STATUS[status] || STATUS.new;
  return <span className="status-pill" style={{ color: s.color, background: s.bg }}>{s.label}</span>;
}

function PipelineProgress({ campaign }: { campaign: NotificationCampaign }) {
  const steps = [
    { key: 'draft', label: 'Draft' },
    { key: 'review', label: 'Legal Review' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'notifying', label: 'Notifying' },
    { key: 'live', label: 'Live' },
  ];
  const statusMap: Record<string, number> = { draft: 0, legal_review: 1, scheduled: 2, notifying: 3, live: 4, complete: 5 };
  const currentIdx = statusMap[campaign.status] ?? 0;
  const fillPct = currentIdx === 0 ? 0 : (currentIdx / (steps.length - 1)) * 100;

  return (
    <div className="notify-pipeline">
      <div className="pipeline-line"><div className="pipeline-line-fill" style={{ width: `${fillPct}%` }} /></div>
      {steps.map((step, i) => (
        <div className="pipeline-step" key={step.key}>
          <div className={`pipeline-dot ${i < currentIdx ? 'complete' : i === currentIdx ? 'active' : ''}`}>
            {i < currentIdx ? '\u2713' : i + 1}
          </div>
          <div className={`pipeline-label ${i < currentIdx ? 'complete' : i === currentIdx ? 'active' : ''}`}>{step.label}</div>
        </div>
      ))}
    </div>
  );
}

function AckProgress({ stats }: { stats: NotificationCampaign['stats'] }) {
  const pct = stats.total > 0 ? Math.round((stats.acknowledged / stats.total) * 100) : 0;
  return (
    <div style={{ margin: '12px 0' }}>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${pct}%`, background: pct === 100 ? C.success : `linear-gradient(135deg, ${C.accent}, ${C.accentLight})` }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: C.textMuted }}>
        <span>{stats.acknowledged} / {stats.total} acknowledged ({pct}%)</span>
        <span>{stats.notified} notified &middot; {stats.pending} pending</span>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ──
export function RegulatoryDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState<RegulatoryAlert[]>([]);
  const [campaigns, setCampaigns] = useState<NotificationCampaign[]>([]);
  const [policies, setPolicies] = useState<RegulatoryPolicy[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<RegulatoryAlert | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [newAction, setNewAction] = useState<{ type: ActionType; assignee: string; note: string; schedule: ActionSchedule }>({
    type: 'policy_update', assignee: 'Stan', note: '', schedule: 'now',
  });

  const [newNotify, setNewNotify] = useState<{ policy: string; version: string; tier: NotifyTier; summary: string; changeSummary: string }>({
    policy: 'pol-tos', version: '', tier: 'moderate', summary: '', changeSummary: '',
  });

  // Load from Firestore
  useEffect(() => {
    let cancelled = false;
    Promise.all([getAllAlerts(), getAllPolicies(), getAllCampaigns()])
      .then(([a, p, c]) => {
        if (!cancelled) { setAlerts(a); setPolicies(p); setCampaigns(c); }
      })
      .catch((err) => console.error('Failed to load regulatory data:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  function showToastMsg(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  async function persistAlert(updated: RegulatoryAlert) {
    try { await saveAlert(updated); } catch (err) { console.error('Failed to save alert:', err); }
  }

  async function addAction(alertId: string) {
    const action: RegulatoryAction = {
      ...newAction,
      date: new Date().toISOString().split('T')[0],
      notifyCampaign: newAction.type === 'policy_update' ? 'pending' : null,
    };

    const updatedAlerts = alerts.map(a => a.id === alertId ? {
      ...a,
      actions: [...a.actions, action],
      status: (a.status === 'new' ? 'reviewed' : a.status === 'reviewed' ? 'in_pipeline' : a.status) as AlertStatus,
    } : a);
    setAlerts(updatedAlerts);
    const updatedAlert = updatedAlerts.find(a => a.id === alertId);
    if (updatedAlert) {
      setSelectedAlert(updatedAlert);
      await persistAlert(updatedAlert);
    }

    setShowActionModal(false);
    setNewAction({ type: 'policy_update', assignee: 'Stan', note: '', schedule: 'now' });

    if (newAction.type === 'policy_update') {
      setTimeout(() => setShowNotifyModal(true), 300);
    } else {
      showToastMsg('Action created');
    }
  }

  async function createCampaign() {
    const tier = NOTIFY_TIERS[newNotify.tier];
    const effectiveDate = new Date();
    effectiveDate.setDate(effectiveDate.getDate() + tier.noticeDays);

    const campaign: NotificationCampaign = {
      id: `camp-${Date.now()}`,
      policyId: newNotify.policy,
      alertId: selectedAlert?.id || null,
      version: newNotify.version || '1.1.0',
      tier: newNotify.tier,
      methods: [...tier.methods],
      status: 'draft',
      summary: newNotify.summary,
      changeSummary: newNotify.changeSummary,
      createdDate: new Date().toISOString().split('T')[0],
      notifyDate: null,
      effectiveDate: effectiveDate.toISOString().split('T')[0],
      stats: { total: 1247, notified: 0, acknowledged: 0, pending: 1247 },
    };

    setCampaigns(prev => [...prev, campaign]);
    try { await saveCampaign(campaign); } catch (err) { console.error('Failed to save campaign:', err); }

    setShowNotifyModal(false);
    setNewNotify({ policy: 'pol-tos', version: '', tier: 'moderate', summary: '', changeSummary: '' });
    showToastMsg(`Notification campaign created \u2014 ${tier.label} tier (${tier.noticeDays}-day notice)`);
  }

  async function advanceCampaignStatus(campId: string) {
    const flow: CampaignStatus[] = ['draft', 'legal_review', 'scheduled', 'notifying', 'live', 'complete'];
    let updated: NotificationCampaign | undefined;

    setCampaigns(prev => prev.map(c => {
      if (c.id !== campId) return c;
      const idx = flow.indexOf(c.status);
      if (idx >= flow.length - 1) return c;
      const nextStatus = flow[idx + 1];
      let newStats = { ...c.stats };
      if (nextStatus === 'notifying') newStats = { ...newStats, notified: Math.round(newStats.total * 0.85), pending: Math.round(newStats.total * 0.15) };
      else if (nextStatus === 'live') newStats = { ...newStats, notified: newStats.total, acknowledged: Math.round(newStats.total * 0.72), pending: 0 };
      else if (nextStatus === 'complete') newStats = { ...newStats, notified: newStats.total, acknowledged: newStats.total, pending: 0 };
      updated = { ...c, status: nextStatus, stats: newStats };
      return updated;
    }));

    if (updated) {
      try { await saveCampaign(updated); } catch (err) { console.error('Failed to save campaign:', err); }
    }
    showToastMsg('Campaign advanced');
  }

  async function cancelCampaign(campId: string) {
    setCampaigns(prev => prev.filter(c => c.id !== campId));
    try { await deleteCampaign(campId); } catch (err) { console.error('Failed to delete campaign:', err); }
    showToastMsg('Campaign cancelled');
  }

  async function updateAlertStatus(alertId: string, newStatus: AlertStatus) {
    const updatedAlerts = alerts.map(a => a.id === alertId ? { ...a, status: newStatus } : a);
    setAlerts(updatedAlerts);
    const updatedAlert = updatedAlerts.find(a => a.id === alertId);
    if (updatedAlert) {
      if (newStatus === 'dismissed') setSelectedAlert(null);
      else setSelectedAlert(updatedAlert);
      await persistAlert(updatedAlert);
    }
    showToastMsg(newStatus === 'dismissed' ? 'Alert dismissed' : 'Status updated');
  }

  const newAlertCount = alerts.filter(a => a.status === 'new').length;
  const activeCampaigns = campaigns.filter(c => c.status !== 'complete');

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, color: C.textMuted }}>Loading regulatory data...</div>;
  }

  if (alerts.length === 0 && policies.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', color: C.textMuted }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F4CB;</div>
        <p style={{ fontSize: 16, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>No regulatory data</p>
        <p style={{ fontSize: 14 }}>Seed regulatory data from the Seed Data page to get started.</p>
      </div>
    );
  }

  return (
    <div className="rd">
      <style>{styles}</style>

      {/* HEADER */}
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="logo">HEMO<span>LOGIX</span></div>
          <span className="header-badge">Regulatory Compliance</span>
        </div>
        <button className="btn btn-ghost" onClick={() => showToastMsg('Digest exported')}>Export Digest</button>
      </div>

      {/* TABS */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'alerts' ? 'active' : ''}`} onClick={() => setActiveTab('alerts')}>
          Alerts {newAlertCount > 0 && <span className="badge">{newAlertCount}</span>}
        </button>
        <button className={`tab ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveTab('notifications')}>
          Policy Updates {activeCampaigns.length > 0 && <span className="badge">{activeCampaigns.length}</span>}
        </button>
        <button className={`tab ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>
          Compliance Calendar
        </button>
        <button className={`tab ${activeTab === 'sources' ? 'active' : ''}`} onClick={() => setActiveTab('sources')}>
          Source Health
        </button>
      </div>

      {/* CONTENT */}
      <div className="content">

        {/* ALERTS TAB */}
        {activeTab === 'alerts' && (
          <div className="card">
            <div className="card-header">
              <h3>Regulatory Alerts</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {Object.entries(STATUS).slice(0, 4).map(([key, val]) => {
                  const count = alerts.filter(a => a.status === key).length;
                  return count > 0 ? (
                    <span key={key} style={{ fontSize: 11, color: val.color, background: val.bg, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                      {val.label}: {count}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
            {alerts.sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 4) - (URGENCY_ORDER[b.urgency] ?? 4)).map(alert => (
              <div key={alert.id} className="alert-row" onClick={() => setSelectedAlert(alert)}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: URGENCY[alert.urgency]?.color, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{alert.title}</span>
                <Pill color={CATEGORIES[alert.category]?.color || C.textMuted} bg={`${CATEGORIES[alert.category]?.color || C.textMuted}15`}>
                  {CATEGORIES[alert.category]?.label || alert.category}
                </Pill>
                {alert.actions.some(a => a.notifyCampaign) && (
                  <span style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>{'\uD83D\uDCCB'} Notify</span>
                )}
                <StatusPill status={alert.status} />
                <span style={{ fontSize: 11, color: C.textMuted, minWidth: 80, textAlign: 'right' }}>{alert.published}</span>
              </div>
            ))}
          </div>
        )}

        {/* POLICY UPDATES TAB */}
        {activeTab === 'notifications' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
              {policies.map(pol => (
                <div key={pol.id} className="card" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 4 }}>{pol.name}</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: pol.currentVersion ? C.text : C.textMuted }}>{pol.currentVersion || 'Not Published'}</div>
                  {pol.effectiveDate && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Effective {pol.effectiveDate}</div>}
                </div>
              ))}
              <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', border: `2px dashed ${C.border}`, background: 'transparent' }}
                onClick={() => { setShowNotifyModal(true); setSelectedAlert(null); }}>
                <div style={{ fontSize: 20, color: C.textMuted, marginBottom: 4 }}>+</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted }}>NEW CAMPAIGN</div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3>Notification Campaigns</h3>
                <span style={{ fontSize: 11, color: C.textMuted }}>{campaigns.length} total</span>
              </div>
              {campaigns.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
                  No notification campaigns yet. Create one from an alert action or click "New Campaign" above.
                </div>
              ) : campaigns.map(camp => {
                const policy = policies.find(p => p.id === camp.policyId);
                const tier = NOTIFY_TIERS[camp.tier];
                const isExpanded = selectedCampaign === camp.id;
                return (
                  <div key={camp.id} className="notify-card">
                    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                      onClick={() => setSelectedCampaign(isExpanded ? null : camp.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: tier.color, background: tier.bg, padding: '3px 8px', borderRadius: 4 }}>{tier.label.toUpperCase()}</span>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{policy?.name} &rarr; v{camp.version}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {camp.methods.map(m => <span key={m} title={METHOD_LABELS[m].label} style={{ fontSize: 14 }}>{METHOD_LABELS[m].icon}</span>)}
                        </div>
                        <span className="status-pill" style={{
                          color: camp.status === 'draft' ? '#9A8A8E' : camp.status === 'live' ? C.success : camp.status === 'notifying' ? C.accent : C.amber,
                          background: camp.status === 'draft' ? 'rgba(154,138,142,0.12)' : camp.status === 'live' ? 'rgba(74,222,128,0.12)' : camp.status === 'notifying' ? C.accentSoft : 'rgba(251,191,36,0.12)',
                        }}>{camp.status.replace('_', ' ')}</span>
                        <span style={{ fontSize: 16, color: C.textMuted, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>{'\u25BE'}</span>
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${C.borderLight}`, paddingTop: 14 }}>
                        <PipelineProgress campaign={camp} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, margin: '16px 0' }}>
                          <div>
                            <div className="form-label">Change Summary</div>
                            <div className="detail-text" style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{camp.changeSummary || camp.summary}</div>
                          </div>
                          <div>
                            <div className="form-label">Notification Methods</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {(Object.entries(METHOD_LABELS) as [NotifyMethod, typeof METHOD_LABELS[NotifyMethod]][]).map(([key, m]) => (
                                <div key={key} className={`method-chip ${camp.methods.includes(key) ? 'active' : 'inactive'}`} style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
                                  <span>{m.icon}</span> {m.label}
                                  {camp.methods.includes(key) && <span style={{ marginLeft: 'auto', fontWeight: 400, opacity: 0.7 }}>{'\u2713'}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, margin: '16px 0' }}>
                          <div style={{ background: C.bgSoft, padding: 12, borderRadius: 8, textAlign: 'center' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase' }}>Notice Period</div>
                            <div style={{ fontSize: 18, fontWeight: 600, color: tier.color, marginTop: 4 }}>{tier.noticeDays} days</div>
                          </div>
                          <div style={{ background: C.bgSoft, padding: 12, borderRadius: 8, textAlign: 'center' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase' }}>Created</div>
                            <div style={{ fontSize: 14, fontWeight: 500, marginTop: 6 }}>{camp.createdDate}</div>
                          </div>
                          <div style={{ background: C.bgSoft, padding: 12, borderRadius: 8, textAlign: 'center' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase' }}>Effective Date</div>
                            <div style={{ fontSize: 14, fontWeight: 500, marginTop: 6, color: C.accent }}>{camp.effectiveDate}</div>
                          </div>
                        </div>
                        {camp.stats.total > 0 && (
                          <div><div className="form-label">User Acknowledgment</div><AckProgress stats={camp.stats} /></div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                          {camp.status !== 'complete' && camp.status !== 'live' && (
                            <button className="btn btn-ghost btn-sm" onClick={() => cancelCampaign(camp.id)}>Cancel</button>
                          )}
                          {camp.status !== 'complete' && (
                            <button className="btn btn-accent btn-sm" onClick={() => advanceCampaignStatus(camp.id)}>
                              {camp.status === 'draft' ? 'Send to Legal Review' : camp.status === 'legal_review' ? 'Approve & Schedule' : camp.status === 'scheduled' ? 'Begin Notifying Users' : camp.status === 'notifying' ? 'Publish Live' : 'Mark Complete'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CALENDAR TAB */}
        {activeTab === 'calendar' && (
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#C9A96E', marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>Compliance Calendar</h3>
            <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>Active regulatory deadlines and upcoming requirements</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[...alerts].filter(a => a.status !== 'dismissed' && a.status !== 'resolved')
                .sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 4) - (URGENCY_ORDER[b.urgency] ?? 4))
                .map(a => (
                  <div key={a.id} className="calendar-item" style={{ borderLeftColor: URGENCY[a.urgency]?.color || C.textMuted }}
                    onClick={() => { setActiveTab('alerts'); setSelectedAlert(a); }}>
                    <div style={{ minWidth: 80 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: URGENCY[a.urgency]?.color, letterSpacing: '.3px' }}>{URGENCY[a.urgency]?.label}</div>
                      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3, fontFamily: "'DM Mono', monospace" }}>{a.published}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 3 }}>{a.title}</div>
                      <div style={{ fontSize: 12, color: C.textMuted }}>{a.estimated_timeline || a.summary.slice(0, 80) + '...'}</div>
                    </div>
                    <StatusPill status={a.status} />
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* SOURCES TAB */}
        {activeTab === 'sources' && (
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#C9A96E', marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>Source Health</h3>
            <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>Monitoring 12 regulatory sources &mdash; scraped every Sunday at 11pm ET</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SOURCES.map((src, i) => (
                <div key={i} className="source-row" style={src.status === 'degraded' ? { background: 'rgba(220,38,38,0.12)', borderColor: 'rgba(220,38,38,0.25)' } : {}}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: src.status === 'ok' ? C.success : '#F87171', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{src.name}</div>
                    {src.error && <div style={{ fontSize: 11, color: '#F87171', marginTop: 2 }}>{src.error}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted, fontFamily: "'DM Mono', monospace", textAlign: 'right' }}>
                    <div>{src.last}</div>
                    <div style={{ color: C.border }}>{src.items} items</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>Test</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ALERT DETAIL PANEL */}
      {selectedAlert && (
        <div className="detail-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSelectedAlert(null); }}>
          <div className="detail-panel">
            <div className="detail-header">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <Pill color={URGENCY[selectedAlert.urgency]?.color || C.textMuted} bg={URGENCY[selectedAlert.urgency]?.bg || C.borderLight}>
                  {URGENCY[selectedAlert.urgency]?.label}
                </Pill>
                <button className="btn btn-ghost btn-sm" onClick={() => setSelectedAlert(null)}>{'\u2715'} Close</button>
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 400, lineHeight: 1.3, margin: 0, color: '#C9A96E' }}>{selectedAlert.title}</h2>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                <Pill color={CATEGORIES[selectedAlert.category]?.color || C.textMuted} bg={`${CATEGORIES[selectedAlert.category]?.color || C.textMuted}15`}>
                  {CATEGORIES[selectedAlert.category]?.label || selectedAlert.category}
                </Pill>
                <StatusPill status={selectedAlert.status} />
                <span style={{ fontSize: 11, color: C.textMuted }}>{selectedAlert.source} &middot; {selectedAlert.published}</span>
              </div>
            </div>

            <div className="detail-body">
              <div className="detail-section">
                <div className="detail-section-title">Summary</div>
                <p className="detail-text">{selectedAlert.summary}</p>
              </div>

              <div className="detail-section">
                <div className="detail-section-title">Affected Systems</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {selectedAlert.affected_systems.map(s => <span key={s} className="action-tag">{SYSTEM_LABELS[s] || s.replace(/_/g, ' ')}</span>)}
                </div>
              </div>

              {/* Actions */}
              <div className="detail-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div className="detail-section-title" style={{ marginBottom: 0 }}>Actions</div>
                  <button className="btn btn-accent btn-sm" onClick={() => setShowActionModal(true)}>+ Add Action</button>
                </div>
                {selectedAlert.actions.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: C.textMuted, fontSize: 12, background: C.bgSoft, borderRadius: 8 }}>No actions yet. Add one to begin the workflow.</div>
                ) : selectedAlert.actions.map((action, i) => {
                  const at = ACTION_TYPES[action.type];
                  const linkedCampaign = (action.notifyCampaign && action.notifyCampaign !== 'pending') ? campaigns.find(c => c.id === action.notifyCampaign) : null;
                  return (
                    <div key={i} className="action-item">
                      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{at?.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: at?.color, marginBottom: 2 }}>{at?.label}</div>
                        <div style={{ fontSize: 12, color: C.textMuted }}>{action.note}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                          <span className="action-tag">{action.assignee}</span>
                          <span className="action-tag">{action.date}</span>
                          <span className="action-tag">
                            {action.schedule === 'now' ? '\u26A1 Immediate' : action.schedule === 'legal_first' ? '\u2696\uFE0F Legal First' : '\uD83D\uDCC5 Scheduled'}
                          </span>
                        </div>
                        {action.type === 'policy_update' && (
                          <div style={{ marginTop: 8, padding: '8px 10px', background: C.accentSoft, borderRadius: 6, border: '1px solid rgba(196,54,90,0.2)' }}>
                            {linkedCampaign ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>
                                  {'\uD83D\uDCCB'} Notify campaign: {NOTIFY_TIERS[linkedCampaign.tier]?.label} tier &rarr; {linkedCampaign.status.replace('_', ' ')}
                                </span>
                                <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 10 }}
                                  onClick={() => { setActiveTab('notifications'); setSelectedAlert(null); setSelectedCampaign(linkedCampaign.id); }}>
                                  View &rarr;
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 11, color: C.textMuted }}>{'\u26A0\uFE0F'} No notification campaign linked</span>
                                <button className="btn btn-accent btn-sm" style={{ padding: '2px 8px', fontSize: 10 }} onClick={() => setShowNotifyModal(true)}>Configure Notify &rarr;</button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Status Actions */}
              <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.borderLight}` }}>
                {selectedAlert.status !== 'resolved' && selectedAlert.status !== 'dismissed' && (
                  <>
                    <button className="btn btn-primary" onClick={() => {
                      const flow: Record<string, AlertStatus> = { new: 'reviewed', reviewed: 'in_pipeline', in_pipeline: 'resolved' };
                      const next = flow[selectedAlert.status];
                      if (next) updateAlertStatus(selectedAlert.id, next);
                    }}>
                      {selectedAlert.status === 'new' ? 'Mark Reviewed' : selectedAlert.status === 'reviewed' ? 'Move to Pipeline' : 'Resolve'}
                    </button>
                    <button className="btn btn-ghost" onClick={() => updateAlertStatus(selectedAlert.id, 'dismissed')}>Dismiss</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD ACTION MODAL */}
      {showActionModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowActionModal(false); }}>
          <div className="modal">
            <h3>Add Action</h3>
            <div className="form-group">
              <div className="form-label">Action Type</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {Object.entries(ACTION_TYPES).map(([key, val]) => (
                  <div key={key} onClick={() => setNewAction(prev => ({ ...prev, type: key as ActionType }))}
                    style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${newAction.type === key ? val.color : C.border}`, background: newAction.type === key ? `${val.color}08` : 'transparent', transition: 'all 0.15s' }}>
                    <span style={{ fontSize: 14 }}>{val.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 6, color: newAction.type === key ? val.color : C.textSec }}>{val.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {newAction.type === 'policy_update' && (
              <div style={{ padding: 10, background: C.accentSoft, borderRadius: 8, marginBottom: 16, fontSize: 12, color: C.accent }}>
                {'\uD83D\uDCCB'} Creating a Policy Update action will prompt you to configure a user notification campaign next.
              </div>
            )}
            <div className="form-group">
              <div className="form-label">Assign To</div>
              <select className="form-select" value={newAction.assignee} onChange={e => setNewAction(prev => ({ ...prev, assignee: e.target.value }))}>
                {['Stan', 'Engineering', 'Legal', 'Marketing', 'Dr. Calapai'].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="form-group">
              <div className="form-label">Note</div>
              <textarea className="form-input" placeholder="What needs to be done..." value={newAction.note} onChange={e => setNewAction(prev => ({ ...prev, note: e.target.value }))} />
            </div>
            <div className="form-group">
              <div className="form-label">When</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {([{ key: 'now' as const, label: '\u26A1 Now' }, { key: 'scheduled' as const, label: '\uD83D\uDCC5 Schedule' }, { key: 'legal_first' as const, label: '\u2696\uFE0F Legal First' }]).map(opt => (
                  <div key={opt.key} onClick={() => setNewAction(prev => ({ ...prev, schedule: opt.key }))}
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center', cursor: 'pointer', border: `2px solid ${newAction.schedule === opt.key ? C.accent : C.border}`, background: newAction.schedule === opt.key ? C.accentSoft : 'transparent', fontSize: 12, fontWeight: 600, color: newAction.schedule === opt.key ? C.accent : C.textSec }}>
                    {opt.label}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={() => setShowActionModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { if (selectedAlert) addAction(selectedAlert.id); }}>
                {newAction.type === 'policy_update' ? 'Create & Configure Notify \u2192' : 'Create Action'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATION CAMPAIGN MODAL */}
      {showNotifyModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowNotifyModal(false); }}>
          <div className="modal" style={{ width: 560 }}>
            <h3>Configure User Notification</h3>
            <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20, marginTop: -12 }}>Set up how affected users will be notified of this policy change.</p>
            <div className="form-group">
              <div className="form-label">Policy Document</div>
              <select className="form-select" value={newNotify.policy} onChange={e => setNewNotify(prev => ({ ...prev, policy: e.target.value }))}>
                {policies.map(p => <option key={p.id} value={p.id}>{p.name} (current: {p.currentVersion || 'none'})</option>)}
              </select>
            </div>
            <div className="form-group">
              <div className="form-label">New Version Number</div>
              <input className="form-input" placeholder="e.g. 1.1.0" value={newNotify.version} onChange={e => setNewNotify(prev => ({ ...prev, version: e.target.value }))} />
            </div>
            <div className="form-group">
              <div className="form-label">Change Severity</div>
              {(Object.entries(NOTIFY_TIERS) as [NotifyTier, typeof NOTIFY_TIERS[NotifyTier]][]).map(([key, tier]) => (
                <div key={key} className={`tier-option ${newNotify.tier === key ? 'selected' : ''}`} onClick={() => setNewNotify(prev => ({ ...prev, tier: key }))}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: tier.color, marginBottom: 2 }}>{tier.label}</div>
                      <div style={{ fontSize: 12, color: C.textMuted }}>{tier.description}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: tier.color }}>{tier.noticeDays}d</div>
                      <div style={{ fontSize: 10, color: C.textMuted }}>notice</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {(Object.entries(METHOD_LABELS) as [NotifyMethod, typeof METHOD_LABELS[NotifyMethod]][]).map(([mKey, m]) => (
                      <span key={mKey} className={`method-chip ${tier.methods.includes(mKey) ? 'active' : 'inactive'}`}>{m.icon} {m.label}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="form-group">
              <div className="form-label">Summary for Users</div>
              <textarea className="form-input" placeholder="Brief description of what changed..." value={newNotify.summary} onChange={e => setNewNotify(prev => ({ ...prev, summary: e.target.value }))} />
            </div>
            <div className="form-group">
              <div className="form-label">Detailed Change Log</div>
              <textarea className="form-input" placeholder={'• Added Section X\n• Updated data sharing language\n• New deletion rights...'} style={{ minHeight: 100 }}
                value={newNotify.changeSummary} onChange={e => setNewNotify(prev => ({ ...prev, changeSummary: e.target.value }))} />
            </div>
            <div style={{ background: C.bgSoft, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Notification Preview</div>
              <div style={{ fontSize: 12, color: C.textSec, lineHeight: 1.6 }}>
                <strong>Tier:</strong> {NOTIFY_TIERS[newNotify.tier].label} &mdash; {NOTIFY_TIERS[newNotify.tier].noticeDays}-day notice period<br />
                <strong>Methods:</strong> {NOTIFY_TIERS[newNotify.tier].methods.map(m => METHOD_LABELS[m].label).join(' \u2192 ')}<br />
                <strong>Effective:</strong> ~{new Date(Date.now() + NOTIFY_TIERS[newNotify.tier].noticeDays * 86400000).toLocaleDateString()}<br />
                {newNotify.tier === 'material' && (
                  <span style={{ color: C.accent, fontWeight: 600 }}>{'\u26A0'} Users will be blocked from using the app until they accept the updated terms.</span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setShowNotifyModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={createCampaign}>Create Notification Campaign</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{'\u2713'} {toast}</div>}
    </div>
  );
}
